import React, { useEffect, useState } from "react"
import PubSub from "pubsub-js"
import { store } from "aldoo-ra/Store"
import Button from "aldoo-ra/Button"
import Img from "../Img"
import PriceDisplay from "../PriceDisplay"
import CartStepper from "./CartStepper"
import useT from "../../hooks/useT"
import { useCart, openCart } from "../../store/cart"
import { useCurrency } from "../../store/currency"
import { cartSubtotal, lineKey, lineProduct, lineUnitPrice, productCurrency } from "../../checkout/cartItems"
import { buildContentUrl } from "../../utils/contentRoutes"

export const RAIL_CLASS = "cart-rail-open"

/**
 * The cart at the right edge of a wide screen, the way the big shops keep it
 * in view: the subtotal, a way into the cart, and every line with its
 * picture, price and stepper. Shown while the cart holds something and the
 * cart drawer is closed; the page makes room for it (Layout.css).
 */
export default function CartRail() {
  const t = useT()
  const lines = useCart()
  const { currency, convert } = useCurrency()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    setDrawerOpen(!!store.cartVisible)
    const opened = PubSub.subscribe("STORE_CART_OPENED", () => setDrawerOpen(true))
    const closed = PubSub.subscribe("STORE_CART_CLOSED", () => setDrawerOpen(false))
    return () => {
      PubSub.unsubscribe(opened)
      PubSub.unsubscribe(closed)
    }
  }, [])

  const shown = mounted && lines.length > 0 && !drawerOpen
  useEffect(() => {
    document.documentElement.classList.toggle(RAIL_CLASS, shown)
    return () => document.documentElement.classList.remove(RAIL_CLASS)
  }, [shown])

  if (!shown) return null
  const subtotal = cartSubtotal(lines, convert)
  return (
    <aside
      className="hidden xl:flex fixed top-0 right-0 bottom-0 z-[410] w-[130px] flex-col border-l border-bordered bg-paper"
      aria-label={t`Cart`}
      data-testid="cart-rail"
    >
      {/* the arrow at the cart icon in the top bar, which the rail stands beside */}
      <span aria-hidden className="absolute -left-[9px] top-[22px] w-0 h-0 border-y-[8px] border-y-transparent border-r-[9px] border-r-paper" />
      <span aria-hidden className="absolute -left-[10px] top-[22px] w-0 h-0 border-y-[8px] border-y-transparent border-r-[9px] border-r-bordered -z-[1]" />
      <div className="shrink-0 flex flex-col items-center gap-2 px-2 py-3 border-b border-bordered">
        <span className="text-[13px] text-text_secondary">{t`Subtotal`}</span>
        <PriceDisplay amount={subtotal} currency={currency} variant="medium" className="justify-center min-w-0 text-text" />
        <Button 
          variant="primary_dark_s" 
          className="w-full min-w-0 px-1 text-[13px] whitespace-nowrap" 
          onClick={openCart} 
          data-testid="cart-rail-open"
        >
          {t`Go to Cart`}
        </Button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin scrollbar-thumb-neutral_2 scrollbar-track-paper">
        {/* the newest first: the cart keeps the lines in the order they were added */}
        {[...lines].reverse().map((line) => {
          const product = lineProduct(line)
          return (
            <div key={lineKey(line)} className="flex flex-col items-center gap-2 px-3 py-3 border-b border-bordered" data-testid="cart-rail-line">
              <Button variant="none" href={buildContentUrl(product)} className="block w-[80px] h-[80px] rounded overflow-hidden transition-transform duration-200 hover:scale-110" aria-label={line.name}>
                <Img src={product.square || line.thumbnail} alt={line.name} width={80} height={80} className="w-full h-full" />
              </Button>
              <PriceDisplay amount={convert(lineUnitPrice(line), productCurrency(product))} currency={currency} variant="small" className="justify-center min-w-0" />
              <CartStepper product={product} line={line} className="max-w-[110px] h-[32px] border border-bordered" placement="cart_rail" />
            </div>
          )
        })}
      </div>
    </aside>
  )
}
